import * as React from 'react';
import { applyRunwayTheme, RUNWAY_THEME_CSS_VARS } from './globalStyles';

import { updateNavTypeUserProperty } from '../../utils/analytics';

export type ThemeMode = 'light' | 'mixed' | 'dark';

const STORAGE_KEY = 'runway.themeMode';

function getInitialThemeMode(): ThemeMode {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (stored === 'light' || stored === 'mixed' || stored === 'dark') {
            return stored;
        }
    } catch (_e) {
        // localStorage unavailable
    }

    return 'light';
}

let _currentMode: ThemeMode = getInitialThemeMode();

export function getCurrentThemeMode(): ThemeMode {
    return _currentMode;
}

export function getCurrentSidebarTheme(): 'light' | 'dark' {
    return _currentMode === 'light' ? 'light' : 'dark';
}

export function getCurrentSidebarColors(): SidebarColors {
    return SIDEBAR_COLORS;
}

type ThemeModeContextValue = {
    mode: ThemeMode;
    setMode: (mode: ThemeMode) => void;
};

const defaultContextValue: ThemeModeContextValue = {
    mode: 'light',
    setMode: () => {},
};

const ThemeModeContext = React.createContext<ThemeModeContextValue>(defaultContextValue);

export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [mode, setMode] = React.useState<ThemeMode>(() => getInitialThemeMode());

    const handleSetMode = React.useCallback((newMode: ThemeMode) => {
        _currentMode = newMode;
        setMode(newMode);
        updateNavTypeUserProperty(newMode);
    }, []);

    React.useEffect(() => {
        const dataTheme = mode === 'dark' ? 'dark' : 'light';
        const sidebarTheme = getCurrentSidebarTheme();
        document.documentElement.setAttribute('data-theme', dataTheme);
        document.documentElement.setAttribute('data-color-mode', dataTheme);
        document.documentElement.setAttribute('data-sidebar-theme', sidebarTheme);
        applyRunwayTheme(dataTheme as 'light' | 'dark');

        try {
            window.localStorage.setItem(STORAGE_KEY, mode);
        } catch {
            // ignore
        }
    }, [mode]);

    return <ThemeModeContext.Provider value={{ mode, setMode: handleSetMode }}>{children}</ThemeModeContext.Provider>;
};

export function useThemeMode(): ThemeModeContextValue {
    return React.useContext(ThemeModeContext);
}

export function useSidebarTheme(): 'light' | 'dark' {
    const { mode } = useThemeMode();
    return mode === 'light' ? 'light' : 'dark';
}

export interface SidebarColors {
    bg: string;
    // bgSecondary: string;
    dropdownBg: string;
    border: string;
    text: string;
    textSecondary: string;
    textMuted: string;
    icon: string;
    iconMuted: string;
    selectedBg: string;
    connectorBg: string;
    segmentedBg: string;
    segmentedActiveBg: string;
    menuHoverBg: string;
    menuActiveBg: string;
    dropdownHoverBg: string;
    headerControlsBg: string;
    skippedReleaseDateText: string;
    releaseDateText: string;
    stepChildrenActiveBg: string;
    platformMuted: string;
    searchBg: string;
    tagSelectedBg: string;
    tagSelectedText: string;
}

// All sidebar colors are driven by CSS variables scoped to data-sidebar-theme in index.less.
// A single object references those variables — no need for separate light/dark objects.
const SIDEBAR_COLORS: SidebarColors = {
    bg: 'var(--rw-sidebar-bg)',
    dropdownBg: 'var(--rw-sidebar-dropdown-bg)',
    border: 'var(--rw-sidebar-border)',
    text: 'var(--rw-sidebar-text)',
    textSecondary: 'var(--rw-sidebar-text-secondary)',
    textMuted: 'var(--rw-sidebar-text-muted)',
    icon: 'var(--rw-sidebar-icon)',
    iconMuted: 'var(--rw-sidebar-icon-muted)',
    selectedBg: 'var(--rw-sidebar-selected-bg)',
    menuHoverBg: 'var(--rw-sidebar-menu-hover-bg)',
    menuActiveBg: 'var(--rw-sidebar-menu-active-bg)',
    dropdownHoverBg: 'var(--rw-sidebar-dropdown-hover-bg)',
    connectorBg: 'var(--rw-sidebar-connector-bg)',
    segmentedBg: 'var(--rw-sidebar-segmented-bg)',
    segmentedActiveBg: 'var(--rw-sidebar-segmented-active-bg)',
    headerControlsBg: 'var(--rw-sidebar-header-controls-bg)',
    skippedReleaseDateText: 'var(--rw-sidebar-skipped-release-date-text)',
    releaseDateText: 'var(--rw-sidebar-release-date-text)',
    stepChildrenActiveBg: 'var(--rw-sidebar-step-children-active-bg)',
    platformMuted: 'var(--rw-sidebar-platform-muted)',
    searchBg: 'var(--rw-sidebar-search-bg)',
    tagSelectedBg: 'var(--rw-sidebar-tag-selected-bg)',
    tagSelectedText: 'var(--rw-sidebar-tag-selected-text)',
};

export function useSidebarColors(): SidebarColors {
    return SIDEBAR_COLORS;
}

export function useChartTheme(): 'dark' | 'default' {
    const { mode } = useThemeMode();
    return mode === 'dark' ? 'dark' : 'default';
}
export interface ChartColors {
    gridLine: string;
    axisLabel: string;
    annotationText: string;
    tooltipBg: string;
    tooltipText: string;
    secondarySeries: string;
}

// Chart colors use hardcoded hex because canvas-based charts cannot parse CSS variables.
// Values are derived from RUNWAY_THEME_CSS_VARS (the single source of truth for theme colors).
function rgbVarToHex(rgbString: string): string {
    const parts = rgbString.split(',').map((s) => parseInt(s.trim(), 10));
    return '#' + parts.map((p) => p.toString(16).padStart(2, '0')).join('');
}

function chartColorsFromTheme(mode: 'light' | 'dark'): ChartColors {
    const vars = RUNWAY_THEME_CSS_VARS[mode];
    return {
        gridLine: rgbVarToHex(vars['--rw-ant-grey-4-rgb']),
        axisLabel: rgbVarToHex(vars['--rw-ant-grey-7-rgb']),
        annotationText: rgbVarToHex(vars['--rw-ant-grey-8-rgb']),
        tooltipBg: rgbVarToHex(vars['--rw-ant-grey-8-rgb']),
        tooltipText: rgbVarToHex(vars['--rw-ant-grey-1-rgb']),
        secondarySeries: rgbVarToHex(vars['--rw-ant-grey-6-rgb']),
    };
}

const CHART_COLORS_LIGHT: ChartColors = chartColorsFromTheme('light');
const CHART_COLORS_DARK: ChartColors = chartColorsFromTheme('dark');

export function useChartColors(): ChartColors {
    const { mode } = useThemeMode();
    return mode === 'dark' ? CHART_COLORS_DARK : CHART_COLORS_LIGHT;
}

/**
 * Hardcoded hex color palettes for canvas-based chart rendering (AntV G2/G2Plot).
 * Canvas charts cannot parse CSS variables, so these must be raw hex strings.
 *
 * Each palette provides shades 1-10 matching the @ant-design/colors output.
 */
export interface ChartColorPalette {
    1: string;
    2: string;
    3: string;
    4: string;
    5: string;
    6: string;
    7: string;
    8: string;
    9: string;
    10: string;
}

export interface ChartStatCategoryPalettes {
    gold: ChartColorPalette;
    magenta: ChartColorPalette;
    cyan: ChartColorPalette;
    blue: ChartColorPalette;
    /** Area fill color for chart backgrounds */
    areaFill: string;
}

const CHART_STAT_PALETTES_LIGHT: ChartStatCategoryPalettes = {
    gold: {
        1: '#fffbe6',
        2: '#fff1b8',
        3: '#ffe58f',
        4: '#ffd666',
        5: '#ffc53d',
        6: '#faad14',
        7: '#d48806',
        8: '#ad6800',
        9: '#874d00',
        10: '#613400',
    },
    magenta: {
        1: '#fff0f6',
        2: '#ffd6e7',
        3: '#ffadd2',
        4: '#ff85c0',
        5: '#f759ab',
        6: '#eb2f96',
        7: '#c41d7f',
        8: '#9e1068',
        9: '#780650',
        10: '#520339',
    },
    cyan: {
        1: '#e6fffb',
        2: '#b5f5ec',
        3: '#87e8de',
        4: '#5cdbd3',
        5: '#36cfc9',
        6: '#13c2c2',
        7: '#08979c',
        8: '#006d75',
        9: '#00474f',
        10: '#002329',
    },
    blue: {
        1: '#e6f4ff',
        2: '#bae0ff',
        3: '#91caff',
        4: '#69b1ff',
        5: '#4096ff',
        6: '#1677ff',
        7: '#0958d9',
        8: '#003eb3',
        9: '#002c8c',
        10: '#001d66',
    },
    areaFill: rgbVarToHex(RUNWAY_THEME_CSS_VARS['light']['--rw-ant-grey-7-rgb']),
};

const CHART_STAT_PALETTES_DARK: ChartStatCategoryPalettes = {
    gold: {
        1: '#271c0d',
        2: '#402d0d',
        3: '#563e11',
        4: '#795611',
        5: '#a87612',
        6: '#d79513',
        7: '#e7b338',
        8: '#f3cc62',
        9: '#f8df8b',
        10: '#faecb5',
    },
    magenta: {
        1: '#240f1c',
        2: '#3c132b',
        3: '#511938',
        4: '#721d4c',
        5: '#9e2467',
        6: '#ca2a82',
        7: '#e0529b',
        8: '#f37fb7',
        9: '#f8a8cc',
        10: '#fad2e3',
    },
    cyan: {
        1: '#0d1d1e',
        2: '#0d3132',
        3: '#104545',
        4: '#116060',
        5: '#128383',
        6: '#12a7a7',
        7: '#32bcb6',
        8: '#58d1c9',
        9: '#83e1d8',
        10: '#b2f0e8',
    },
    blue: {
        1: '#0d1628',
        2: '#0e2142',
        3: '#112e57',
        4: '#123e7b',
        5: '#1453ab',
        6: '#1567db',
        7: '#3b89e7',
        8: '#65a9f3',
        9: '#8dc4f8',
        10: '#b7dcfa',
    },
    areaFill: rgbVarToHex(RUNWAY_THEME_CSS_VARS['dark']['--rw-ant-grey-7-rgb']),
};

export function useChartStatCategoryPalettes(): ChartStatCategoryPalettes {
    const { mode } = useThemeMode();
    return mode === 'dark' ? CHART_STAT_PALETTES_DARK : CHART_STAT_PALETTES_LIGHT;
}
